import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { BreadcrumbComponent } from '../../../blocks/breadcrumb/breadcrumb.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexStates,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  ChartComponent
} from 'ng-apexcharts';
import { NgScrollbar } from 'ngx-scrollbar';
import { OkeeyCardStatusService } from 'core/services/okeey-card-status.service';
import { KorviuLoaderComponent } from 'shared';
import { BalanceService } from 'core';
import { ReferralModalFacade } from '../../../shared/services/referral-modal.facade';
import { environment } from '../../../../../environments/environment';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { HideTooltipOnScrollDirective } from 'shared/directives';
import { NO_PERSISTENT_CHART_SELECTION_EVENTS, NO_PERSISTENT_CHART_SELECTION_STATES } from 'shared/types/chart.config';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { ResourceLoadErrorComponent } from 'shared/components/resource-load-error/resource-load-error.component';
import { formatBalanceDate } from 'shared/utils/balance-format.utils';
import { isDeletedAccountName } from 'shared/utils/deleted-account.utils';

const TABLE_COLUMNS = [
  { key: 'period', label: 'balance.period' },
  { key: 'spending', label: 'balance.mySpending', desktopOnly: true },
  { key: 'bonus_ganas', label: 'balance.bonusGanas', desktopOnly: true },
  { key: 'total', label: 'balance.totalGanasEarned' }
];
const EARNINGS_TABLE_PAGE_SIZE = 6;
const EARNINGS_CATEGORIES = [
  { key: 'my_spending', translationKey: 'mySpending', color: '#007DCB' },
  { key: 'my_community', translationKey: 'myCommunity', color: '#8AC3E7' },
  { key: 'bonus_ganas', translationKey: 'bonusGanas', color: '#005990' },
  { key: 'received_shares', translationKey: 'receivedShares', color: '#005990' }
] as const;

interface IEarningsTableRowView {
  period: string;
  spending: string;
  bonus_ganas: string;
  total: string;
}

type DonutChartOptions = {
  series: ApexNonAxisChartSeries;
  colors: string[];
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  legend: ApexLegend;
  stroke: ApexStroke;
  plotOptions: ApexPlotOptions;
  fill: ApexFill;
  states: ApexStates;
};

type EarningsBarChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  colors: string[];
  plotOptions: ApexPlotOptions;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  grid: ApexGrid;
  legend: ApexLegend;
  dataLabels: ApexDataLabels;
  tooltip: ApexTooltip;
  states: ApexStates;
};

@Component({
  selector: 'app-earnings',
  imports: [
    BreadcrumbComponent,
    TranslatePipe,
    KorviuButtonComponent,
    ChartComponent,
    NgScrollbar,
    KorviuLoaderComponent,
    NzTooltipDirective,
    HideTooltipOnScrollDirective,
    NzPaginationModule,
    ResourceLoadErrorComponent
  ],
  templateUrl: './earnings.component.html',
  styleUrl: './earnings.component.scss'
})
export class EarningsComponent {
  readonly #translateService = inject(TranslateService);
  readonly #referralModalFacade = inject(ReferralModalFacade);
  readonly #balanceService = inject(BalanceService);
  readonly okeeyCardStatusService = inject(OkeeyCardStatusService);

  readonly language = toSignal(this.#translateService.onLangChange.pipe(map(event => event.lang)), {
    initialValue: this.#translateService.currentLang || this.#translateService.defaultLang || 'es'
  });

  readonly lifeTimeEarningsResource = this.#balanceService.getLifetimeEarnings();
  readonly lifetimeEarnings = computed(() =>
    this.lifeTimeEarningsResource.hasValue() ? this.lifeTimeEarningsResource.value() : undefined
  );
  readonly lifetimeEarningsLoading = computed(() => this.lifeTimeEarningsResource.isLoading());

  readonly growingGanasResource = this.#balanceService.getGrowingGanas(this.language);
  readonly growingGanas = computed(() =>
    this.growingGanasResource.hasValue() ? this.growingGanasResource.value() : undefined
  );
  readonly growingGanasLoading = computed(() => this.growingGanasResource.isLoading());
  readonly topCardLoading = computed(() => this.lifetimeEarningsLoading() || this.growingGanasLoading());

  readonly earningsTableResource = this.#balanceService.getEarningsTable(12, this.language);
  readonly earningsTable = computed(() =>
    this.earningsTableResource.hasValue() ? this.earningsTableResource.value() : undefined
  );
  readonly earningsTableLoading = computed(() => this.earningsTableResource.isLoading());
  readonly earningsTablePageSize = EARNINGS_TABLE_PAGE_SIZE;
  readonly earningsTablePage = signal(1);

  readonly earningsByCategoryResource = this.#balanceService.getEarningsByCategory(12, this.language);
  readonly earningsByCategory = computed(() =>
    this.earningsByCategoryResource.hasValue() ? this.earningsByCategoryResource.value() : undefined
  );
  readonly earningsByCategoryLoading = computed(() => this.earningsByCategoryResource.isLoading());
  readonly earningsCategories = computed(() => {
    this.language();
    const data = this.earningsByCategory();
    const hasBonusGanas =
      data?.totals?.bonus_ganas !== undefined ||
      data?.labels?.bonus_ganas !== undefined ||
      data?.months.some(month => month.bonus_ganas !== undefined);
    const hasReceivedShares =
      data?.totals?.received_shares !== undefined ||
      data?.labels?.received_shares !== undefined ||
      data?.months.some(month => month.received_shares !== undefined);

    return EARNINGS_CATEGORIES.filter(category => {
      if (category.key === 'bonus_ganas') return hasBonusGanas;
      if (category.key === 'received_shares') return !hasBonusGanas && hasReceivedShares;

      return true;
    }).map(category => ({
      ...category,
      label: data?.labels?.[category.key] || this.#translateService.instant(`balance.${category.translationKey}`),
      tooltipKey: `balance.${category.translationKey}CategoryTooltip`
    }));
  });

  readonly TABLE_COLUMNS = TABLE_COLUMNS;
  readonly earningsTableRows = computed<IEarningsTableRowView[]>(() =>
    [...(this.earningsTable()?.periods || [])]
      .sort((firstPeriod, secondPeriod) => secondPeriod.period_date.localeCompare(firstPeriod.period_date))
      .map(period => ({
        period: period.period,
        spending: this.formatUsd(Number(period.my_spending_usd)),
        bonus_ganas: this.formatGanas(period.bonus_ganas),
        total: this.formatGanas(period.total_ganas_earned)
      }))
  );
  readonly visibleEarningsTableRows = computed<IEarningsTableRowView[]>(() => {
    const offset = (this.earningsTablePage() - 1) * this.earningsTablePageSize;

    return this.earningsTableRows().slice(offset, offset + this.earningsTablePageSize);
  });

  readonly topContributorsResource = this.#balanceService.getTopContributors(this.language);
  readonly topContributors = computed(() =>
    this.topContributorsResource.hasValue() ? this.topContributorsResource.value() : undefined
  );
  readonly topContributorsPeriodLabel = computed(() => this.topContributors()?.period_label);
  readonly topContributorsResolved = computed(() => this.topContributorsResource.status() === 'resolved');
  readonly topContributorsLoading = computed(() => this.topContributorsResource.isLoading());

  readonly growingGanasAvailabilityLabel = computed(() => {
    const language = this.language();
    const availability = this.growingGanas()?.availability;
    const availabilityDisplay = formatBalanceDate(availability?.date, language) || availability?.display;

    return availabilityDisplay
      ? this.#translateService.instant('balance.availableOnDate', { date: availabilityDisplay })
      : '';
  });
  readonly growingGanasTrendDisplay = computed(() => this.growingGanas()?.trend?.display || '');
  readonly isGrowingGanasTrendUp = computed(() => this.growingGanas()?.trend?.direction === 'up');
  readonly isGrowingGanasTrendDown = computed(() => this.growingGanas()?.trend?.direction === 'down');
  readonly growingGanasTrendColor = computed(() => {
    if (this.isGrowingGanasTrendDown()) {
      return '#D14343';
    }

    if (this.isGrowingGanasTrendUp()) {
      return '#078339';
    }

    return '#8A8E94';
  });
  readonly growingGanasLabels = computed(() => ({
    mySpending: this.growingGanas()?.labels?.my_spending || this.#translateService.instant('balance.mySpending'),
    myCommunity: this.growingGanas()?.labels?.my_community || this.#translateService.instant('balance.myCommunity')
  }));

  readonly topContributorRows = computed(() =>
    (this.topContributors()?.contributors || []).map(contributor => ({
      ...contributor,
      isDeleted: !!contributor.is_deleted || isDeletedAccountName(contributor.name, contributor.user_id),
      avatarUrl:
        contributor.avatar && !contributor.is_deleted && !isDeletedAccountName(contributor.name, contributor.user_id)
          ? this.getAvatarUrl(contributor.avatar)
          : ''
    }))
  );

  readonly donutChartOptions = computed<DonutChartOptions>(() => {
    const growingGanas = this.growingGanas();

    return {
      series: [growingGanas?.my_spending || 0, growingGanas?.my_community || 0],
      colors: ['#007DCB', '#8AC3E7'],

      chart: {
        type: 'donut',
        height: 250,
        width: 250,
        events: NO_PERSISTENT_CHART_SELECTION_EVENTS
      },

      dataLabels: { enabled: false },
      legend: { show: false },
      stroke: { width: 0 },

      plotOptions: {
        pie: {
          donut: {
            size: '60%',
            labels: {
              show: true,
              name: { show: false },
              value: {
                show: true,
                fontSize: '40px',
                fontWeight: 600,
                color: '#333'
              },
              total: {
                show: false,
                label: 'Invitations',
                fontSize: '14px',
                fontWeight: 400,
                color: '#2C3035',
                formatter: () => {
                  return '0';
                }
              }
            }
          }
        }
      },

      fill: { opacity: 1 },
      states: NO_PERSISTENT_CHART_SELECTION_STATES
    };
  });

  readonly earningsBarChartOptions = computed<EarningsBarChartOptions>(() => {
    const earningsByCategory = this.earningsByCategory();
    const months = earningsByCategory?.months || [];
    const categories = this.earningsCategories();

    return {
      series: categories.map(category => ({
        name: category.label,
        data: months.map(month => month[category.key] ?? 0)
      })),
      chart: {
        type: 'bar',
        height: 300,
        stacked: true,
        toolbar: {
          show: false
        },
        events: NO_PERSISTENT_CHART_SELECTION_EVENTS
      },
      colors: categories.map(category => category.color),
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 4,
          borderRadiusApplication: 'end'
        }
      },
      xaxis: {
        categories: months.map(month => month.month),
        labels: {
          style: {
            colors: '#8A8E94',
            fontSize: '14px',
            fontFamily: 'Outfit, sans-serif'
          }
        },
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      },
      yaxis: {
        labels: {
          show: false
        }
      },
      grid: {
        show: false
      },
      legend: {
        show: false,
        position: 'bottom',
        horizontalAlign: 'center',
        markers: {
          size: 14
        },
        itemMargin: {
          horizontal: 20,
          vertical: 10
        },
        labels: {
          colors: '#505760',
          useSeriesColors: false
        }
      },
      dataLabels: {
        enabled: false
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        y: {
          formatter: function (val: number) {
            return val + ' Ganas';
          }
        }
      },
      states: NO_PERSISTENT_CHART_SELECTION_STATES
    };
  });

  public onEarningsTablePageChange(page: number): void {
    this.earningsTablePage.set(page);
  }

  private getAvatarUrl(url: string): string {
    return environment.avatarsUrl + 'Property_1=' + url;
  }

  inviteReferral(): void {
    this.#referralModalFacade.openAddReferralModal();
  }

  private formatUsd(value: number): string {
    return `$${value.toLocaleString(undefined, {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2
    })}`;
  }

  private formatGanas(value: number): string {
    return `${value.toLocaleString()} Ganas`;
  }
}
